/**
 * One capture session, as a row.
 *
 * The approved design puts a strip of photograph thumbnails here and four
 * analysis columns beside them. Three of those four are real; the thumbnails
 * are not, and that difference decides the whole component.
 *
 * **No thumbnails.** The API serves media metadata and never media bytes, so
 * every tile would be a grey placeholder shaped exactly like a preview. That
 * is worse than showing none: it invites the user to believe the system holds
 * something it will show them, and to read a blank tile as a broken file
 * rather than as a missing feature. The strip is replaced by an honest count
 * and a per-file chip list.
 *
 * **A row shows capture facts, not analysis.** Analiz and Kapsama were columns
 * here, which made every row restate a project-level answer and cost one
 * head-coverage request per session to do it. They moved to a single summary
 * at the bottom of the screen. Per-session analysis is not lost - it is behind
 * Çekimi İncele, where somebody debugging one capture will look for it.
 *
 * Counts arrive as a prop rather than being fetched here, so the aggregate at
 * the bottom and the rows above it are the same numbers.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { deleteCaptureSession } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { CaptureSession, MediaAsset, MediaCountSummary } from "@/api/types";
import { IconClock, IconImage, IconVideo } from "@/components/icons";
import { Button, StatusBadge } from "@/components/ui";
import {
  CAPTURE_PURPOSE_LABELS,
  CAPTURE_SESSION_STATUS_LABELS,
  CAPTURE_SESSION_STATUS_TONES,
  CAPTURE_TYPE_LABELS,
} from "@/i18n/terms";
import { formatDateTime } from "@/lib/format";
import { paths } from "@/app/paths";
import "./sessions.css";

/** A labelled state, or an em dash when nothing has been measured yet. */
function Cell({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | null;
  // exactOptionalPropertyTypes: an omitted tone is explicitly undefined.
  tone?: "success" | "caution" | "danger" | "neutral" | undefined;
}) {
  return (
    <div className="sessionrow__cell">
      <span className="sessionrow__cell-label">{label}</span>
      <span className={`sessionrow__cell-value${tone ? ` sessionrow__cell-value--${tone}` : ""}`}>
        {value ?? "—"}
      </span>
    </div>
  );
}

export function SessionRow({
  session,
  assets,
  counts,
  onDeleted,
}: {
  session: CaptureSession;
  /**
   * This session's media, loaded once for the whole screen by
   * `useCaptureSummary`. `null` means still loading - counts render as an em
   * dash rather than as a zero, because zero is a measurement.
   */
  /**
   * One page of this session's media. The thumbnail strip, and nothing else.
   */
  assets: MediaAsset[] | null;
  /**
   * The server's totals for this session.
   *
   * Every number this row shows comes from here, because `assets` is a page:
   * a 150-file session rendered "100 medya", "+94", "Fotoğraf 100" and
   * "Medya 100" when they were counted from it, beside a project card that
   * correctly said 150.
   */
  counts: MediaCountSummary | null;
  /** Called after the server confirmed the delete, so the list can reload. */
  onDeleted?: () => void;
}) {
  // Two steps, in place. A capture holds photographs somebody walked around a
  // person to take, and there is no undo on the server - so the destructive
  // click is never the first one.
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function remove() {
    setBusy(true);
    setFailure(null);
    try {
      await deleteCaptureSession(session.id);
      onDeleted?.();
    } catch (error) {
      // The 409 has a real meaning worth saying out loud; everything else
      // gets one honest sentence rather than a backend string.
      setFailure(
        error instanceof ApiError && error.status === 409
          ? "Bu çekim bir 3B rekonstrüksiyonda kullanıldığı için silinemez."
          : "Çekim silinemedi. Lütfen tekrar deneyin.",
      );
      setBusy(false);
    }
  }

  const settled = assets !== null && counts !== null;
  //: The page, for the six thumbnails and the "+N" beside them.
  const files = assets ?? [];
  //: The session, for every number.
  const total = counts?.total ?? 0;
  const photos = counts?.photos ?? 0;
  const videos = counts?.videos ?? 0;

  return (
    <article className="sessionrow">
      <div className="sessionrow__main">
        <div className="sessionrow__head">
          <span
            className={`sessionrow__dot sessionrow__dot--${CAPTURE_SESSION_STATUS_TONES[session.status]}`}
            aria-hidden="true"
          />
          <h3>
            {CAPTURE_PURPOSE_LABELS[session.purpose]} ·{" "}
            {CAPTURE_TYPE_LABELS[session.capture_type]}
          </h3>
          <StatusBadge tone={CAPTURE_SESSION_STATUS_TONES[session.status]}>
            {CAPTURE_SESSION_STATUS_LABELS[session.status]}
          </StatusBadge>
        </div>
        <p className="sessionrow__meta">
          <IconClock size={14} /> {formatDateTime(session.created_at)}
          {" · "}
          {settled ? `${total} medya` : "—"}
        </p>
        {session.notes && <p className="muted small">{session.notes}</p>}

        {/*
          Where the mockup shows thumbnails. The backend serves no bytes, so
          this lists what is actually known about each file instead of drawing
          placeholders that look like previews.
        */}
        {settled && files.length > 0 && (
          <ul className="sessionrow__files">
            {files.slice(0, 6).map((asset) => (
              <li key={asset.id} className="sessionrow__file" title={asset.original_filename}>
                {asset.media_type === "video" ? <IconVideo size={14} /> : <IconImage size={14} />}
                <span>{asset.file_extension.toUpperCase()}</span>
              </li>
            ))}
            {total > 6 && (
              // Counted against the session, not the page: with a page of 100
              // out of 150 this read "+94" and hid fifty files.
              <li className="sessionrow__file sessionrow__file--more">+{total - 6}</li>
            )}
          </ul>
        )}
      </div>

      {/*
        Capture facts only. Analiz and Kapsama used to sit here too, which made
        every row a small dashboard repeating a project-level answer once per
        session - and cost a head-coverage request per row to do it. They live
        in one summary at the bottom of the screen now. Nothing was deleted
        from the backend; per-session analysis is still reachable through
        Çekimi İncele.
      */}
      <div className="sessionrow__cells">
        <Cell label="Fotoğraf" value={settled ? String(photos) : null} />
        <Cell label="Video" value={settled ? String(videos) : null} />
        <Cell label="Medya" value={settled ? String(total) : null} />
      </div>

      <div className="sessionrow__actions">
        <Link className="btn btn--secondary btn--sm" to={paths.session(session.id)}>
          Çekimi İncele
        </Link>
        {confirming ? (
          <>
            <Button variant="danger" busy={busy} onClick={() => void remove()}>
              Kalıcı olarak sil
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>
              Vazgeç
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={() => setConfirming(true)}>
            Sil
          </Button>
        )}
      </div>
      {confirming && !failure && (
        <p className="sessionrow__warning">
          Bu çekim ve içindeki {total > 0 ? `${total} dosya` : "dosyalar"}{" "}
          kalıcı olarak silinir. Geri alınamaz.
        </p>
      )}
      {failure && (
        <p className="sessionrow__warning" role="alert">
          {failure}
        </p>
      )}
    </article>
  );
}
